import React from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const valuesData = [
    {
        id: 1,
        title: "Passion",
        text: "We’re a passionate team at Mobikode, always exploring better ways to create simple, effective, and reliable technology solutions for our clients.",
        image: "/assets/about/item3.png",
        borderColor: "border-[#f36f21]"
    },
    {
        id: 2,
        title: "Transparency",
        text: "We believe in being open and honest, keeping you informed at every step so you always feel confident and involved.",
        image: "/assets/about/item7.jpg",
        borderColor: "border-[#01386A]"
    },
    {
        id: 3,
        title: "Talent",
        text: "At Mobikode, our people are our real strength. We support their growth so they can create thoughtful, high-quality code every day.",
        image: "/assets/about/item8.png",
        borderColor: "border-[#f36f21]"
    },
    {
        id: 4,
        title: "Insights",
        text: "We use data to better understand your business, uncover new opportunities, and help solve challenges with smarter, more confident decisions.",
        image: "/assets/about/item9.jpg",
        borderColor: "border-[#01386A]"
    }

];

// Completely CSS driven ValueCard (Works perfectly with Slider Clones)
const ValueCard = ({ item }) => {
    return (
        <div className={`group bg-white flex w-full h-[320px] md:h-[350px] shadow-sm border-b-[3px] border-[#f36f21] hover:border-[#01386A] transition-colors duration-300 cursor-pointer overflow-hidden`}>

            {/* Image Section */}
            <div className="w-[45%] h-full shrink-0 overflow-hidden">
                <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            </div>

            {/* Content Section */}
            <div className="w-[55%] p-6 md:p-8 flex flex-col relative bg-white">
                <h3 className="text-[#01386A] text-[16px] md:text-[20px] font-[600] font-sora mb-4 shrink-0">
                    {item.title}
                </h3>

                {/* Container for transitioning text & link */}
                <div className="relative flex-1 w-full overflow-hidden">

                    {/* Hover Text (Slides in from Right) */}
                    <div className="absolute inset-0 translate-x-[120%] opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 ease-out flex items-center pr-2">
                        <p className="text-[#01386A] font-[500] text-[14px] md:text-[16px] leading-[1.6] font-graphik">
                            {item.text}
                        </p>
                    </div>

                    {/* Read More (Fades out on Hover) */}
                    <div className="absolute bottom-0 left-0 flex items-center gap-2 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                        <span className="text-[#01386A] text-[14px] md:text-[15px] font-[600] font-graphik border-b border-[#01386A]">Read More</span>
                        <span className="text-[#f36f21] font-bold text-[16px] leading-none">&gt;</span>
                    </div>

                </div>
            </div>
        </div>
    );
};

const OurValues = () => {
    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        loop: true,
        slides: {
            perView: 1,
            spacing: 20,
        },
        breakpoints: {
            "(min-width: 768px)": {
                slides: { perView: 2, spacing: 30 },
            },
            "(min-width: 1280px)": {
                slides: { perView: 2, spacing: 40 },
            },
        },
    });

    return (
        <section className="bg-[#FAFAFA] py-[60px] lg:py-[100px] overflow-hidden font-sans">
            <div className="max-w-[1440px] mx-auto lg:px-[80px] px-[20px]">
                <div className="mb-12">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="font-[600] font-sora text-[20px] md:text-[30px] leading-[1.2] text-[#01386A] mb-[16px]"
                    >
                        Our Values
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-[#01386A] font-graphik font-[500] text-[14px] lg:text-[16px] leading-[1.6] max-w-[800px]"
                    >
                        We believe in doing things the right way with honesty, quality, and a real passion for what we do. At Mobikode, our focus is simple: helping our clients succeed and grow with confidence.
                    </motion.p>
                </div>

                <div className="relative">
                    <div ref={sliderRef} className="keen-slider py-4">
                        {valuesData.map((item) => (
                            <div key={item.id} className="keen-slider__slide">
                                <ValueCard item={item} />
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-4 mt-8">
                        <button
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                            className="w-[40px] h-[40px] rounded-full border-[1.5px] border-[#f36f21] flex items-center justify-center text-[#f36f21] hover:bg-[#f36f21] hover:text-white transition-all duration-300"
                        >
                            <ChevronLeft size={20} strokeWidth={2} />
                        </button>
                        <button
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                            className="w-[40px] h-[40px] rounded-full border-[1.5px] border-[#f36f21] flex items-center justify-center text-[#f36f21] hover:bg-[#f36f21] hover:text-white transition-all duration-300"
                        >
                            <ChevronRight size={20} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OurValues;